import React from "react";
import {
    AreaChart,
    Area,
    Tooltip,
    XAxis,
    YAxis,
    ResponsiveContainer,
    Dot,
    CartesianGrid,
} from "recharts";

// Format large numbers (e.g., 560000 -> $560K)
const formatNumber = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(3)}M`;
    if (value >= 1000) {
        const thousands = value / 1000;
        return thousands % 1 === 0 ? `$${thousands}K` : `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toLocaleString()}`;
};

const CustomTooltip = ({ active, payload, label, viewMode, averageROI }) => {
    if (active && payload && payload.length) {
        const value = formatNumber(payload[0].value);
        const roiPercentage =
            viewMode === 'cumulative' && payload[0].payload.initialInvestment
                ? ((payload[0].value / payload[0].payload.initialInvestment) * 100).toFixed(1)
                : 0;
        return (
            <div
                style={{
                    backgroundColor: "#1e1e1e",
                    color: "#fff",
                    padding: "8px",
                    borderRadius: "5px",
                    fontFamily: "Aeonik",
                }}
            >
                <p className="text-[#DF0F11] text-[12px] tracking-[1px] font-regular">
                    {label}
                </p>
                <div className="flex items-center">
                    <p className="font-medium text-white text-[14px]">{value}</p>
                    {viewMode === 'cumulative' && (
                        <p className="ml-[20px]" style={{ color: "#0f0", fontSize: "12px" }}>
                            ▲ {roiPercentage}%
                        </p>
                    )}
                </div>
            </div>
        );
    }
    return null;
};

const CustomCursor = ({ points, width, height }) => {
    if (!points || points.length < 2) return null;

    const { x } = points[0];
    const { y } = points[1];

    return (
        <line
            x1={x}
            y1={0}
            x2={x}
            y2={y}
            stroke="#DF0F11"
            strokeDasharray="6 4"
            strokeWidth={2}
        />
    );
};

const InvestmentGrowthChart = ({ averageROI, data, viewMode }) => {
    if (!data || data.length === 0) {
        return (
            <div style={{ width: "100%", height: 350, background: "#000", padding: 20, color: "#fff", textAlign: "center" }}>
                No investment data available.
            </div>
        );
    }

    // Define the fixed 12-month timeline for the current year (2025)
    const currentYear = new Date().getFullYear(); // 2025
    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    // Prepare chart data based on viewMode
    let chartData = data;

    if (viewMode === 'monthly') {
        // Parse the data timeline to extract year and month for each entry
        const parsedData = data.map((entry) => {
            // Assuming month format is "MMM 'YY" (e.g., "Sep '24")
            const [monthStr, yearStr] = entry.month.split(" '");
            const year = parseInt(`20${yearStr}`, 10); // Convert '24' to 2024
            const monthIndex = months.indexOf(monthStr); // Get 0-based month index (e.g., "Sep" -> 8)
            return {
                ...entry,
                year,
                monthIndex,
            };
        });

        // Filter data for the current year (2025) and sort by month
        const dataForCurrentYear = parsedData
            .filter((entry) => entry.year === currentYear)
            .sort((a, b) => a.monthIndex - b.monthIndex);

        // Calculate monthly returns for each month in 2025
        const monthlyReturns = Array(12).fill(0); // Initialize array for 12 months
        const cumulativeValues = Array(12).fill(0); // To store cumulative values for each month

        // Map cumulative values to their respective months
        dataForCurrentYear.forEach((entry) => {
            const monthIdx = entry.monthIndex; // 0-based index (0 for Jan, 11 for Dec)
            cumulativeValues[monthIdx] = entry.cumulative;
        });

        // Calculate monthly returns as the difference between consecutive cumulative values
        for (let i = 0; i < 12; i++) {
            if (cumulativeValues[i] === 0) {
                // If no cumulative value for this month, monthly return is 0
                monthlyReturns[i] = 0;
            } else {
                // Find the previous non-zero cumulative value
                let prevCumulative = 0;
                for (let j = i - 1; j >= 0; j--) {
                    if (cumulativeValues[j] !== 0) {
                        prevCumulative = cumulativeValues[j];
                        break;
                    }
                }
                // Monthly return = current cumulative - previous cumulative
                monthlyReturns[i] = cumulativeValues[i] - prevCumulative;
            }
        }

        // Create chart data with the fixed 12-month timeline
        const monthlyData = months.map((month, index) => {
            const correspondingDataPoint = dataForCurrentYear.find((entry) => entry.monthIndex === index);
            return {
                month: month, // Use the fixed month label (e.g., "Jan")
                cumulative: correspondingDataPoint ? correspondingDataPoint.cumulative : 0,
                // monthly: monthlyReturns[index], // Use the calculated monthly return
                monthly: correspondingDataPoint ? correspondingDataPoint.cumulative : 0, // Use the calculated monthly return
                initialInvestment: correspondingDataPoint ? correspondingDataPoint.initialInvestment : 0,
            };
        });

        chartData = monthlyData;
    }

    return (
        <div
            style={{
                height: 350,
                background: "#000",
                paddingBottom: 0,
            }}
            className="lg:w-full py-5 min-w-[548px]"
        >
            <ResponsiveContainer >
                <AreaChart data={chartData} margin={{ left: 0 }}>
                    <defs>
                        <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#DF0F11" stopOpacity={1} />
                            <stop offset="50%" stopColor="#ff0000" stopOpacity={1} />
                            <stop offset="100%" stopColor="#DF0F11" stopOpacity={1} />
                        </linearGradient>
                        <linearGradient id="fillGradient" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#ff0000" stopOpacity={0.5} />
                            <stop offset="100%" stopColor="#ff0000" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        horizontal={true}
                        vertical={false}
                        stroke="#ffffff"
                        strokeWidth={1}
                        strokeOpacity={0.1}
                    />
                    <XAxis
                        tickLine={false}
                        dataKey="month"
                        stroke="#ffffff"
                        tick={{
                            fontSize: 14,
                            fontFamily: "Aeonik",
                            fill: "#ffffff",
                            dy: 8,
                        }}
                    />
                    <YAxis
                        tickLine={false}
                        stroke="#aaa"
                        tick={{ fontSize: 14, fontFamily: "Aeonik", fill: "#aaa", dy: 4 }}
                        tickFormatter={formatNumber}
                    />
                    <Tooltip content={<CustomTooltip averageROI={averageROI} viewMode={viewMode} />} cursor={<CustomCursor />} />
                    <Area
                        type="monotone"
                        dataKey={viewMode === 'cumulative' ? 'cumulative' : 'monthly'}
                        stroke="url(#lineGradient)"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#fillGradient)"
                        activeDot={(props) => (
                            <Dot
                                {...props}
                                r={5}
                                stroke="#DF0F11"
                                fill="#DF0F11"
                                strokeWidth={3}
                            />
                        )}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default InvestmentGrowthChart;